import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_contract-cleaner/artifacts/7lw9nr1y_logo%20l%27effaceur.jpeg";

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // State for forgot password
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // State for reset password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenEmail, setTokenEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  // Verify token if present
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);
  
  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
      setTokenValid(true);
      setTokenEmail(res.data.email);
    } catch (error) {
      setTokenValid(false);
      toast.error(error.response?.data?.detail || "Lien invalide ou expiré");
    }
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Veuillez entrer votre email");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success("Email envoyé !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: newPassword });
      setResetSuccess(true);
      toast.success("Mot de passe réinitialisé !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };
  
  // RESET PASSWORD VIEW (with token)
  if (token) {
    // Token verification in progress
    if (tokenValid === null) {
      return (
        <div className="min-h-screen bg-void flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
        </div>
      );
    }
    
    // Invalid token
    if (tokenValid === false) {
      return (
        <div className="min-h-screen bg-void flex flex-col items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8 max-w-md w-full text-center border border-red-500/30">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="font-orbitron text-xl font-bold text-white mb-4">Lien Expiré</h2>
            <p className="text-gray-400 mb-6">Ce lien de réinitialisation n'est plus valide. Veuillez en demander un nouveau.</p>
            <Link to="/forgot-password">
              <Button className="bg-cyan-400 text-black hover:bg-cyan-300">Demander un nouveau lien</Button>
            </Link>
          </motion.div>
        </div>
      );
    }
    
    // Reset success
    if (resetSuccess) {
      return (
        <div className="min-h-screen bg-void flex flex-col items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 max-w-md w-full text-center border border-success/30">
            <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
            <h2 className="font-orbitron text-xl font-bold text-white mb-4">Mot de passe réinitialisé !</h2>
            <p className="text-gray-400 mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
            <Link to="/login">
              <Button className="bg-cyan-400 text-black hover:bg-cyan-300">Se connecter</Button>
            </Link>
          </motion.div>
        </div>
      );
    }
    
    // Reset password form
    return (
      <div className="min-h-screen bg-void flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8 max-w-md w-full border border-gray-800">
            <div className="text-center mb-8">
              <img src={LOGO_URL} alt="L'Effaceur" className="w-16 h-16 mx-auto rounded-lg mb-4" />
              <h1 className="font-orbitron text-2xl font-bold text-cyan-400">Nouveau mot de passe</h1>
              <p className="text-gray-500 text-sm mt-2 font-mono">// {tokenEmail}</p>
            </div>
            
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <Label htmlFor="newPassword" className="text-gray-300">Nouveau mot de passe</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 bg-accent border-gray-700 text-white" placeholder="••••••••" required minLength={8} />
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword" className="text-gray-300">Confirmer le mot de passe</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-accent border-gray-700 text-white" placeholder="••••••••" required />
                </div>
              </div>
              
              <Button type="submit" className="w-full bg-cyan-400 text-black hover:bg-cyan-300" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Réinitialiser
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }
  
  // FORGOT PASSWORD VIEW (request email)
  return (
    <div className="min-h-screen bg-void flex flex-col">
      <div className="p-4">
        <Link to="/login" className="inline-flex items-center text-gray-400 hover:text-cyan-400 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />Retour à la connexion
        </Link>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        {emailSent ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 max-w-md w-full text-center border border-success/30">
            <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
            <h2 className="font-orbitron text-xl font-bold text-white mb-4">Email envoyé !</h2>
            <p className="text-gray-400 mb-2">Si un compte existe avec l'adresse <span className="text-cyan-400">{email}</span>, vous recevrez un email avec un lien de réinitialisation.</p>
            <p className="text-gray-500 text-sm mt-4">Vérifiez votre boîte de réception et vos spams.</p>
            <Button onClick={() => setEmailSent(false)} variant="outline" className="mt-6 border-gray-700 text-gray-400">
              Renvoyer l'email
            </Button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8 max-w-md w-full border border-gray-800">
            <div className="text-center mb-8">
              <img src={LOGO_URL} alt="L'Effaceur" className="w-16 h-16 mx-auto rounded-lg mb-4" />
              <h1 className="font-orbitron text-2xl font-bold text-cyan-400">Mot de passe oublié ?</h1>
              <p className="text-gray-500 text-sm mt-2">Entrez votre email pour recevoir un lien de réinitialisation</p>
            </div>
            
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-accent border-gray-700 text-white" placeholder="votre@email.fr" required />
                </div>
              </div>
              
              <Button type="submit" className="w-full bg-cyan-400 text-black hover:bg-cyan-300" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Envoyer le lien
              </Button>
            </form>
            
            <p className="text-center text-gray-500 text-sm mt-6">
              Vous vous souvenez ? <Link to="/login" className="text-cyan-400 hover:underline">Se connecter</Link>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
